import axios, { AxiosError } from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';

// In production the API is served same-origin (nginx proxies /api on the same
// host as the static client), so use a RELATIVE base. This makes the app work
// on whatever origin it is served from — apex or www — with no cross-origin
// request and therefore no CORS "Network Error". In dev the client (5173) and
// API (3001) run on different ports, so honor the absolute VITE_API_URL.
export const apiBaseUrl = import.meta.env.PROD ? '' : (import.meta.env.VITE_API_URL ?? '');

export const api: AxiosInstance = axios.create({
  baseURL: `${apiBaseUrl}/api/v1`,
  withCredentials: true,
  timeout: 30_000,
});

// ─── Request interceptor
api.interceptors.request.use((config) => {
  // Space for future token-based auth (currently session-based)
  return config;
});

export interface ApiErrorShape {
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

// ─── Response interceptor: unwrap { data: ... } envelope
api.interceptors.response.use(
  <T>(response: AxiosResponse<{ data: T }>) => {
    // Unwrap only when the body is the { data: ... } envelope, so a legitimate
    // null payload (e.g. logged-out /auth/me → { data: null }) stays null
    // instead of falling through to the whole envelope object.
    const body = response.data as unknown;
    const unwrapped =
      body && typeof body === 'object' && 'data' in body
        ? (body as { data: T }).data
        : (body as T);
    return { ...response, data: unwrapped } as AxiosResponse<T>;
  },
  async (error: AxiosError<ApiErrorShape>) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      // Session expired — dispatch a global event that AuthGuard reacts to
      window.dispatchEvent(new CustomEvent('rosita:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export function extractError(err: unknown, fallback: string): string {
  if (axios.isAxiosError<ApiErrorShape>(err)) {
    // 413 is usually rejected by Nginx, not the API, so there's no JSON body
    // to read a message from — Axios would leave us with "Request failed with
    // status code 413".
    if (err.response?.status === 413 && !err.response.data?.message) {
      return 'Os ficheiros são demasiado grandes para um só envio. Tenta enviar menos ficheiros de cada vez.';
    }
    return err.response?.data?.message ?? err.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
